import { useCallback, useState } from "react";
import { useIntl } from "react-intl";

import Contact from "./contact";
import AddressRow from "./addressRow";
import Countries from "../../../app/generic/countries";
import InputItem from "../../../app/generic/form/inputItem";
import { getZipCodePattern } from "../../../../modules/dataFunctions";

export const nameField = "name";

export const parseAddressForm = (values, inputId = "") => ({
  name: values[`name${inputId}`],
  street: values[`street${inputId}`],
  streetNumber: values[`streetNumber${inputId}`],
  city: values[`city${inputId}`],
  zip: values[`zipCode${inputId}`],
  country: values[`country${inputId}`],
});

export const parseInitialAddressValues = (address, inputId = "") => {
  const values = getDefaultAddressForm(inputId);
  if (address) {
    values[`name${inputId}`] = address.name;
    values[`street${inputId}`] = address.street;
    values[`streetNumber${inputId}`] = address.streetNumber;
    values[`city${inputId}`] = address.city;
    values[`zipCode${inputId}`] = address.zip;
    values[`country${inputId}`] = address.country || "CZ";
  }
  return values;
};

export const getDefaultAddressForm = (inputId = "") => {
  const values = {};
  values[`country${inputId}`] = "CZ";
  return values;
};

export const setAddressForm = (form, address, inputId = "") => {
  form.setFieldsValue(parseInitialAddressValues(address, inputId));
};

const Address = ({
  form,
  disabled,
  addressType,
  showContact,
  initialAddress,
  formItemLayout,
}) => {
  const intl = useIntl();
  const [inputId] = useState(addressType || "");

  const [zipPattern, setZipPattern] = useState(
    getZipCodePattern((initialAddress && initialAddress.country) || "CZ")
  );
  const [selectedCountry, setSelectedCountry] = useState();
  const handleCountrySelect = useCallback(
    (value) => {
      setSelectedCountry(value);
      setZipPattern(getZipCodePattern(value));
      form.validateFields([`zipCode${inputId}`]);
    },
    [form, inputId]
  );

  const handleZipCodeChange = useCallback(
    (e) => {
      const value = e.target.value.toUpperCase();
      return !selectedCountry ||
        selectedCountry === "GB" ||
        selectedCountry.code === "NL"
        ? value
        : value.replace(new RegExp(" ", "g"), "").trim();
    },
    [selectedCountry]
  );

  return (
    <>
      <InputItem
        disabled={disabled}
        label={intl.formatMessage({
          id: "address.name",
          defaultMessage: "Název",
        })}
        autoComplete="organization"
        fieldName={`name${inputId}`}
        message={intl.formatMessage({
          id: "address.name.empty",
          defaultMessage: "Zadejte prosím název!",
        })}
        formItemLayout={formItemLayout}
      />
      <AddressRow
        firstChildren={
          <InputItem
            maxLength={40}
            disabled={disabled}
            label={intl.formatMessage({
              id: "address.street",
              defaultMessage: "Ulice",
            })}
            autoComplete="street-address"
            fieldName={`street${inputId}`}
            formItemLayout={formItemLayout}
            message={intl.formatMessage({
              id: "address.street.empty",
              defaultMessage: "Zadejte prosím název ulice!",
            })}
          />
        }
        secondChildren={
          <InputItem
            disabled={disabled}
            label={intl.formatMessage({
              id: "address.streetNumber",
              defaultMessage: "Číslo popisné",
            })}
            formItemLayout={formItemLayout}
            fieldName={`streetNumber${inputId}`}
            message={intl.formatMessage({
              id: "address.streetNumber.empty",
              defaultMessage: "Zadejte prosím číslo popisné!",
            })}
          />
        }
      />
      <InputItem
        maxLength={40}
        disabled={disabled}
        label={intl.formatMessage({
          id: "address.city",
          defaultMessage: "Město",
        })}
        fieldName={`city${inputId}`}
        autoComplete="address-level2"
        message={intl.formatMessage({
          id: "address.city.empty",
          defaultMessage: "Zadejte prosím město!",
        })}
        formItemLayout={formItemLayout}
      />
      <AddressRow
        secondBigger
        firstChildren={
          <InputItem
            disabled={disabled}
            pattern={zipPattern}
            autoComplete="postal-code"
            fieldName={`zipCode${inputId}`}
            onValueChange={handleZipCodeChange}
            label={intl.formatMessage({
              id: "address.zipCode",
              defaultMessage: "PSČ",
            })}
            message={intl.formatMessage({
              id: "address.zipCode.empty",
              defaultMessage: "Zadejte prosím PSČ!",
            })}
          />
        }
        secondChildren={
          <Countries
            span={24}
            disabled={disabled}
            onSelect={handleCountrySelect}
            fieldName={`country${inputId}`}
          />
        }
      />
      {showContact && (
        <Contact
          form={form}
          inputId={inputId}
          formItemLayout={formItemLayout}
          initialPreselection={initialAddress.preselection}
        />
      )}
    </>
  );
};

export default Address;
